import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";

const gbp = (n) => `${n < 0 ? "−" : ""}£${Math.abs(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qs = (r) => new URLSearchParams(Object.entries(r).filter(([, v]) => v)).toString();

export function PropertyPnLPanel() {
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get(`/finance/property-summary?${qs(range)}`).then((r) => setRows(r.data || [])).catch(() => {}); }, [range]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><div className="ns-label mb-1">From</div><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="bg-white w-40" data-testid="finance-pnl-from" /></div>
        <div><div className="ns-label mb-1">To</div><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="bg-white w-40" data-testid="finance-pnl-to" /></div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="finance-pnl-grid">
        {rows.map((r) => (
          <button key={r.property_id} onClick={() => setOpenId(r.property_id)} data-testid={`finance-pnl-tile-${r.property_id}`}
            className="ns-card p-4 text-left hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]">
            <div className="text-sm font-medium truncate">{r.property_name}</div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className={`font-serif text-xl ${r.net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{gbp(r.net)}</span>
              {r.net < 0 ? <TrendingDown className="w-4 h-4 text-rose-500" /> : <TrendingUp className="w-4 h-4 text-emerald-500" />}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">In {gbp(r.income)} · Out {gbp(r.expenses)}</div>
          </button>
        ))}
      </div>
      <PropertyLedgerDialog propertyId={openId} range={range} onClose={() => setOpenId(null)} />
    </div>
  );
}

function PropertyLedgerDialog({ propertyId, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (propertyId) api.get(`/finance/property-ledger/${propertyId}?${qs(range)}`).then((r) => setData(r.data)).catch(() => {});
  }, [propertyId, range]);
  return (
    <Dialog open={!!propertyId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="finance-ledger-dialog">
        <DialogHeader><DialogTitle className="font-serif">{data?.property?.name || "Ledger"}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-slate-400">Loading…</div> : (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              <Stat label="Income" value={gbp(data.income)} testId="finance-ledger-income" />
              <Stat label="Expenses" value={gbp(data.expenses)} testId="finance-ledger-expenses" />
              <Stat label="Net" value={gbp(data.net)} testId="finance-ledger-net" />
              <Stat label="ROI" value={data.roi_pct === null ? "—" : `${data.roi_pct}%`} testId="finance-ledger-roi" />
            </div>
            <OccupancyTrend propertyId={propertyId} />
            <table className="w-full text-xs" data-testid="finance-ledger-table">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2">Date</th><th>Type</th><th>Description</th><th className="text-right">Amount</th><th className="text-right">Running</th>
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400 italic">No entries in this window</td></tr>}
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5 text-slate-500">{r.date}</td>
                    <td className="capitalize">{(r.source || "").replace(/_/g, " ")}</td>
                    <td className="text-slate-600 truncate max-w-[260px]">{r.description}</td>
                    <td className={`text-right font-mono ${r.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{gbp(r.amount)}</td>
                    <td className="text-right font-mono">{gbp(r.running_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={testId}>
    <div className="ns-label">{label}</div>
    <div className="font-serif text-lg">{value}</div>
  </div>
);

function OccupancyTrend({ propertyId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/finance/occupancy-trend/${propertyId}?months=12`).then((r) => setRows(r.data.months)).catch(() => {}); }, [propertyId]);
  if (!rows.length) return null;
  const quiet = rows.filter((r) => r.quiet).map((r) => r.month);
  return (
    <div className="rounded-xl border border-slate-100 p-3 mb-4" data-testid="finance-occupancy-trend">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="ns-label">Occupancy & nightly revenue · last 12 months</span>
        {quiet.length > 0 && <span className="text-amber-700" data-testid="finance-occupancy-quiet">Quiet (&lt;50%): {quiet.join(", ")}</span>}
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(m) => m.slice(2)} />
            <YAxis yAxisId="occ" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
            <YAxis yAxisId="rev" orientation="right" tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v, n) => (n === "occupancy_pct" ? [`${v}%`, "Occupancy"] : [`£${v}`, "Revenue / night"])} />
            <Bar yAxisId="occ" dataKey="occupancy_pct" radius={[4, 4, 0, 0]}>
              {rows.map((r) => <Cell key={r.month} fill={r.quiet ? "#f59e0b" : "#0f766e"} />)}
            </Bar>
            <Line yAxisId="rev" type="monotone" dataKey="revpan" stroke="#1e293b" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
